/**
 * Turkish Character Validation Utilities
 * Supports Turkish characters: ğ, ü, ş, ı, ö, ç (and uppercase variants)
 */

// Regex that allows Turkish characters in text fields (names, titles, descriptions, etc.)
export const TURKISH_TEXT_REGEX = /^[a-zA-ZğüşıöçĞÜŞİÖÇ\s\-'.]+$/;

// Regex for names (full name, first name, last name) - allows Turkish chars + spaces + hyphens
export const TURKISH_NAME_REGEX = /^[a-zA-ZğüşıöçĞÜŞİÖÇ\s\-']+$/;

// Regex for general text content (titles, descriptions, subjects) - allows Turkish chars + common punctuation
export const TURKISH_CONTENT_REGEX = /^[a-zA-ZğüşıöçĞÜŞİÖÇ0-9\s\-'".,:;!?()&@#%+/\\*_\n\r]+$/;

// Regex for location/address fields - allows Turkish chars + numbers + common address chars
export const TURKISH_ADDRESS_REGEX = /^[a-zA-ZğüşıöçĞÜŞİÖÇ0-9\s\-'".,:;/\\()]+$/;

// Regex for username - alphanumeric + underscore + dot (no Turkish chars for usernames)
export const USERNAME_REGEX = /^[a-zA-Z0-9_.\-]+$/;

/**
 * Validates that a text field contains valid Turkish characters
 * @param {string} value - The value to validate
 * @param {string} fieldName - The display name of the field (in Turkish)
 * @returns {string|null} - Error message or null if valid
 */
export const validateTurkishText = (value, fieldName) => {
  if (!value?.trim()) return null; // Let required validation handle empty
  if (!TURKISH_NAME_REGEX?.test(value?.trim())) {
    return `${fieldName} yalnızca harf ve Türkçe karakter içerebilir`;
  }
  return null;
};

/**
 * Converts a name string to Title Case with Turkish character support.
 * Each word's first letter is uppercased, the rest are lowercased.
 * Handles Turkish-specific characters (İ/i, I/ı).
 * @param {string} value
 * @returns {string}
 */
export const toTitleCaseTurkish = (value) => {
  if (!value) return '';
  return value?.split(' ')?.map(word => {
      if (!word) return '';
      const first = word?.charAt(0);
      const rest = word?.slice(1);
      // Turkish uppercase: 'i' → 'İ', rest standard
      const upper = first === 'i' ? 'İ' : first?.toLocaleUpperCase('tr-TR');
      // Turkish lowercase: 'I' → 'ı', 'İ' → 'i', rest standard
      const lower = rest?.toLocaleLowerCase('tr-TR');
      return upper + lower;
    })?.join(' ');
};

/**
 * Turkish-compliant Title Case formatter.
 * Alias of toTitleCaseTurkish — use this as the canonical utility across the app.
 * Handles: İ/i, I/ı, Ş/ş, Ç/ç, Ğ/ğ, Ü/ü, Ö/ö
 * @param {string} str
 * @returns {string}
 */
export const toTurkishTitleCase = (str) => toTitleCaseTurkish(str);

/**
 * Validates a full name field with Turkish character support
 * Requires at least two words (first name + last name)
 * @param {string} value
 * @returns {string|null}
 */
export const validateFullName = (value) => {
  if (!value?.trim()) return 'Ad soyad gereklidir';
  if (value?.trim()?.length < 3) return 'Ad soyad en az 3 karakter olmalıdır';
  if (!TURKISH_NAME_REGEX?.test(value?.trim())) {
    return 'Ad soyad yalnızca harf ve Türkçe karakter (ğ, ü, ş, ı, ö, ç) içerebilir';
  }
  const parts = value?.trim()?.split(/\s+/)?.filter(p => p?.length > 0);
  if (parts?.length < 2) {
    return 'Lütfen ad ve soyadınızı giriniz';
  }
  return null;
};

/**
 * Validates a name field (single name) with Turkish character support
 * @param {string} value
 * @param {string} fieldLabel
 * @returns {string|null}
 */
export const validateName = (value, fieldLabel = 'İsim') => {
  if (!value?.trim()) return `${fieldLabel} gereklidir`;
  if (!TURKISH_NAME_REGEX?.test(value?.trim())) {
    return `${fieldLabel} yalnızca harf ve Türkçe karakter (ğ, ü, ş, ı, ö, ç) içerebilir`;
  }
  return null;
};

/**
 * Validates a text content field (titles, descriptions, subjects) with Turkish support
 * @param {string} value
 * @param {string} fieldLabel
 * @param {number} minLength
 * @returns {string|null}
 */
export const validateTurkishContent = (value, fieldLabel = 'Alan', minLength = 1) => {
  if (!value?.trim()) return `${fieldLabel} gereklidir`;
  if (value?.trim()?.length < minLength) return `${fieldLabel} en az ${minLength} karakter olmalıdır`;
  return null;
};

/**
 * Validates an address/location field with Turkish character support
 * @param {string} value
 * @param {string} fieldLabel
 * @returns {string|null}
 */
export const validateTurkishAddress = (value, fieldLabel = 'Adres') => {
  if (!value?.trim()) return `${fieldLabel} gereklidir`;
  if (!TURKISH_ADDRESS_REGEX?.test(value?.trim())) {
    return `${fieldLabel} geçersiz karakter içeriyor`;
  }
  return null;
};

/**
 * Validates email address
 * @param {string} value
 * @returns {string|null}
 */
export const validateEmail = (value) => {
  if (!value?.trim()) return 'E-posta adresi gereklidir';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) {
    return 'Geçerli bir e-posta adresi giriniz';
  }
  return null;
};

/**
 * Validates Turkish phone number
 * @param {string} value
 * @param {boolean} required
 * @returns {string|null}
 */
export const validatePhone = (value, required = true) => {
  if (!value?.trim()) {
    return required ? 'Telefon numarası gereklidir' : null;
  }
  const digitsOnly = value?.replace(/[\s\-\(\)]/g, '');
  if (!/^[0-9+]+$/?.test(digitsOnly)) {
    return 'Telefon numarası yalnızca rakam içerebilir';
  }
  // Accept 11 digits starting with 0 (Turkish mobile: 05XX XXX XX XX)
  // or 10 digits without leading 0
  if (digitsOnly?.length === 11 && digitsOnly?.startsWith('0')) {
    return null;
  }
  if (digitsOnly?.length === 10 && !digitsOnly?.startsWith('0')) {
    return null;
  }
  if (digitsOnly?.length < 10) {
    return 'Telefon numarası 11 haneli olmalıdır (0532 123 45 67)';
  }
  return 'Geçerli bir Türk telefon numarası giriniz (0532 123 45 67)';
};

export default {
  validateFullName,
  validateName,
  validateTurkishText,
  validateTurkishContent,
  validateTurkishAddress,
  validateEmail,
  validatePhone,
  toTitleCaseTurkish,
  toTurkishTitleCase,
  TURKISH_NAME_REGEX,
  TURKISH_TEXT_REGEX,
  TURKISH_CONTENT_REGEX,
  TURKISH_ADDRESS_REGEX,
};
